import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { MenuProps, Spin } from 'antd'
import { FileExcelOutlined, FileImageOutlined, FilePdfOutlined } from '@ant-design/icons'
import { CustomReport, PrintStyleSettings } from '../../../../../redux/context/customReports/types'
import { useCtxUpdateCustomReport } from '../../../../../features/customReport/queries/report/useCtxUpdateCustomReport'
import { ResponsiveLayouts } from '../grid.utils'
import { contextCompanyIdSelector, contextCompanySelector } from '../../../../../redux/context/company/selectors'
import { contextContractIdSelector } from '../../../../../redux/context/contract/selectors'
import { authTokenSelector } from '../../../../../redux/session/authentication/selectors'
import {
  customReportDateSelector,
  customReportArgumentsSelector
} from '../../../../../redux/context/customReports/selectors'
import { appLanguageSelector } from '../../../../../redux/app/selectors'
import { useGridExport } from '../grid/useGridExport'
import { getTitleByLanguage } from '../customReportTable/utils'
import { customReportSectionOptions } from '../../../../../features/customReport/queries/section/customReportSectionOptions'
import { buildReportFileName } from '../../../../../utils/buildReportFileName'
import { showErrorMessage } from '../../../../../utils/message'
import useCustomReportExport from '../../hooks/useCustomReportExport'
import PrintDrawer from './PrintDrawer'

interface CtxPrintPreviewProps {
  report: CustomReport
  disabled?: boolean
  layouts?: ResponsiveLayouts
}

const CtxPrintPreview: React.FC<CtxPrintPreviewProps> = ({ report, disabled, layouts }) => {
  const companyId = useSelector(contextCompanyIdSelector)!
  const company = useSelector(contextCompanySelector)
  const contractId = useSelector(contextContractIdSelector)
  const token = useSelector(authTokenSelector)
  const date = useSelector(customReportDateSelector)
  const language = useSelector(appLanguageSelector)
  const args = useSelector(customReportArgumentsSelector)

  const { mutateAsync: updateCustomReport, isPending: saveLoading } = useCtxUpdateCustomReport()
  const { exportAllToExcel, allRegistered } = useGridExport()
  const queryClient = useQueryClient()
  const [exporting, setExporting] = useState(false)

  const { download: exportToFile, isLoading: downloadLoading } = useCustomReportExport({
    reportId: report.id,
    companyId,
    companyName: company?.name,
    contractId,
    date,
    language,
    args
  })

  const handleSavePrintSettings = useCallback(
    (settings: PrintStyleSettings) => {
      updateCustomReport({
        data: { id: report.id, style: { ...report.style, print: settings } },
        options: { showSuccess: true, skipInvalidation: true }
      })
    },
    [report.id, report.style, updateCustomReport]
  )

  const handleExcelExport = useCallback(async () => {
    try {
      const reportName = report.name ? getTitleByLanguage(report) : 'report'
      const fileName = buildReportFileName(company?.name, reportName, date, 'xlsx')
      await exportAllToExcel(fileName, layouts?.lg)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Export failed'
      showErrorMessage(errorMessage)
    } finally {
      setExporting(false)
    }
  }, [report, company, date, exportAllToExcel, layouts])

  useEffect(() => {
    if (exporting && allRegistered) {
      handleExcelExport()
    }
  }, [exporting, allRegistered, handleExcelExport])

  const startExcelExport = useCallback(async () => {
    setExporting(true)
    try {
      const tableSections = report.sections?.filter(s => s.type === 'table') ?? []
      await Promise.all(
        tableSections.map(s =>
          queryClient.ensureQueryData(
            customReportSectionOptions(companyId, s.reportId, s.id, { date, language, args }, true)
          )
        )
      )
    } catch {
      setExporting(false)
      showErrorMessage(undefined)
    }
  }, [report, companyId, date, language, args, queryClient])

  const handleDownloadMenuClick = useCallback(
    ({ key }: { key: string }) => {
      const reportName = report.name ? getTitleByLanguage(report) : 'report'
      switch (key) {
        case 'pdf':
          exportToFile({ printSettings: { ...report.style?.print, outputFormat: 'pdf' }, reportName })
          break
        case 'png':
          exportToFile({ printSettings: { ...report.style?.print, outputFormat: 'png' }, reportName })
          break
        case 'jpg':
          exportToFile({ printSettings: { ...report.style?.print, outputFormat: 'jpg' }, reportName })
          break
        case 'xlsx':
          startExcelExport()
          break
        default:
          break
      }
    },
    [report, exportToFile, startExcelExport]
  )

  const downloadMenu: MenuProps = {
    items: [
      {
        label: 'PDF',
        key: 'pdf',
        icon: <FilePdfOutlined />,
        disabled: !report.id || downloadLoading,
        'data-cy': 'download-pdf'
      },
      {
        label: 'PNG',
        key: 'png',
        icon: <FileImageOutlined />,
        disabled: !report.id || downloadLoading,
        'data-cy': 'download-png'
      },
      {
        label: 'JPG',
        key: 'jpg',
        icon: <FileImageOutlined />,
        disabled: !report.id || downloadLoading,
        'data-cy': 'download-jpg'
      },
      {
        label: 'Excel',
        key: 'xlsx',
        disabled: !report.id || exporting,
        icon: exporting ? <Spin style={{ fontSize: '0.6rem' }} /> : <FileExcelOutlined />,
        'data-cy': 'download-excel'
      }
    ],
    onClick: handleDownloadMenuClick
  }

  return (
    <PrintDrawer
      report={report}
      disabled={disabled}
      layouts={layouts}
      companyId={companyId}
      companyName={company?.name}
      contractId={contractId}
      token={token}
      date={date}
      language={language}
      args={args}
      onSavePrintSettings={handleSavePrintSettings}
      saveLoading={saveLoading}
      downloadMenu={downloadMenu}
      downloadLoading={downloadLoading || exporting}
    />
  )
}

export default CtxPrintPreview
